import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, BookOpen, LayoutGrid, FileCheck, UtensilsCrossed, ShoppingBag, CreditCard, Brain, MonitorSmartphone } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import Navbar from '@/components/smartpos/Navbar';
import Footer from '@/components/smartpos/Footer';
import FloatingCTA from '@/components/smartpos/FloatingCTA';
import { Image } from '@/components/ui/image';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import JsonLd from '@/components/smartpos/JsonLd';
import ErrorState from '@/components/smartpos/ErrorState';
import { useLanguage } from '@/lib/LanguageContext';

const categories = ['Todos', 'Guías', 'VeriFactu', 'Hostelería', 'Retail', 'Pagos', 'IA', 'Hardware'];

const categoryIcons = {
  'Todos': LayoutGrid, 'Guías': BookOpen, 'VeriFactu': FileCheck, 'Hostelería': UtensilsCrossed,
  'Retail': ShoppingBag, 'Pagos': CreditCard, 'IA': Brain, 'Hardware': MonitorSmartphone,
};

const ui = {
  ES: { resources: 'Recursos', heroTitle1: 'Blog', heroTitle2: 'SmartPOS', heroDesc: 'Guías prácticas para modernizar tu negocio, cumplir la normativa y vender más.', metaTitle: 'Blog SmartPOS | Guías TPV, VeriFactu y gestión para negocios', metaDesc: 'Guías prácticas sobre TPV, VeriFactu, cajones inteligentes, quioscos y carta digital. Consejos para hostelería, retail, alimentación y belleza.', empty: 'No hay artículos en esta categoría todavía.', read: 'Leer artículo', loadMore: 'Ver más artículos', loading: 'Cargando...', min: 'min' },
  EN: { resources: 'Resources', heroTitle1: 'Blog', heroTitle2: 'SmartPOS', heroDesc: 'Practical guides to modernize your business, comply with regulations and sell more.', metaTitle: 'SmartPOS Blog | POS Guides, VeriFactu and Business Management', metaDesc: 'Practical guides on POS, VeriFactu, smart drawers, kiosks and digital menu. Tips for hospitality, retail, food and beauty.', empty: 'No articles in this category yet.', read: 'Read article', loadMore: 'See more articles', loading: 'Loading...', min: 'min' },
  CA: { resources: 'Recursos', heroTitle1: 'Blog', heroTitle2: 'SmartPOS', heroDesc: 'Guies pràctiques per modernitzar el teu negoci, complir la normativa i vendre més.', metaTitle: 'Blog SmartPOS | Guies TPV, VeriFactu i gestió per a negocis', metaDesc: 'Guies pràctiques sobre TPV, VeriFactu, calaixos intel·ligents, quioscos i carta digital. Consells per a hostaleria, retail, alimentació i bellesa.', empty: 'No hi ha articles en aquesta categoria encara.', read: 'Llegir article', loadMore: 'Veure més articles', loading: 'Carregant...', min: 'min' },
  FR: { resources: 'Ressources', heroTitle1: 'Blog', heroTitle2: 'SmartPOS', heroDesc: 'Guides pratiques pour moderniser votre entreprise, respecter la réglementation et vendre plus.', metaTitle: 'Blog SmartPOS | Guides TPV, VeriFactu et gestion d\'entreprise', metaDesc: 'Guides pratiques sur TPV, VeriFactu, tiroirs intelligents, kiosques et menu digital. Conseils pour hôtellerie, retail, alimentation et beauté.', empty: 'Pas encore d\'articles dans cette catégorie.', read: 'Lire l\'article', loadMore: 'Voir plus d\'articles', loading: 'Chargement...', min: 'min' },
};

export default function Blog() {
  const { lang, t: tGlobal } = useLanguage();
  const t = ui[lang] || ui.ES;
  useDocumentMeta({ title: t.metaTitle, description: t.metaDesc, canonicalPath: '/blog' });

  const blogJsonLd = {
    '@context': 'https://schema.org', '@type': 'Blog', name: 'Blog SmartPOS',
    description: t.metaDesc, url: 'https://www.smartpos.es/blog',
    publisher: { '@type': 'Organization', name: 'SmartPOS', url: 'https://www.smartpos.es' },
  };

  const PAGE_SIZE = 12;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activeCat, setActiveCat] = useState('Todos');
  const [error, setError] = useState(false);

  const fetchPosts = async (category, skip, append) => {
    if (!append) { setLoading(true); setError(false); } else setLoadingMore(true);
    try {
      const query = { published: true };
      if (category !== 'Todos') query.category = category;
      const data = await base44.entities.BlogPost.filter(query, '-published_date', PAGE_SIZE, skip);
      if (append) {
        setPosts(prev => {
          const ids = new Set(prev.map(p => p.id));
          const fresh = data.filter(p => !ids.has(p.id));
          setHasMore(fresh.length > 0);
          return [...prev, ...fresh];
        });
      } else {
        setPosts(data);
        setHasMore(data.length === PAGE_SIZE);
      }
    } catch {
      if (append) {
        setHasMore(false);
      } else {
        setPosts([]);
        setError(true);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchPosts(activeCat, 0, false); }, [activeCat]);
  const loadMore = () => fetchPosts(activeCat, posts.length, true);

  return (
    <div className="min-h-screen font-body bg-white">
      <JsonLd data={blogJsonLd} />
      <Navbar />
      <main id="contenido-principal">
      <section className="pt-32 pb-12 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-brand-blue mb-5 px-3 py-1.5 bg-blue-50 rounded-full border border-blue-100">
            <BookOpen size={12} /> {t.resources}
          </span>
          <h1 className="font-heading font-light text-4xl lg:text-5xl text-gray-900 mb-4 tracking-tight">
            {t.heroTitle1} <span className="text-brand-blue font-medium">{t.heroTitle2}</span>
          </h1>
          <p className="text-gray-500 text-lg font-light max-w-2xl mx-auto">{t.heroDesc}</p>
        </div>
      </section>

      <div className="border-b border-gray-100 py-4">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap gap-2 justify-center">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              aria-pressed={activeCat === cat}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                activeCat === cat ? 'bg-brand-blue text-white shadow-md shadow-blue-500/20' : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-900 border border-gray-200'
              }`}
            >
              {(() => { const Icon = categoryIcons[cat]; return Icon ? <Icon size={14} className="shrink-0" /> : null; })()}
              {cat}
            </button>
          ))}
        </div>
      </div>

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="rounded-2xl bg-white border border-gray-100 overflow-hidden shadow-sm animate-pulse">
                  <div className="aspect-video bg-gray-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-20 bg-gray-100 rounded" />
                    <div className="h-5 w-full bg-gray-100 rounded" />
                    <div className="h-3 w-full bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <ErrorState
              message={tGlobal('data.loadError')}
              retryLabel={tGlobal('data.retry')}
              onRetry={() => fetchPosts(activeCat, 0, false)}
            />
          ) : posts.length === 0 ? (
            <p className="text-center text-gray-400 py-20 font-light">{t.empty}</p>
          ) : (
            <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group rounded-2xl bg-white border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 flex flex-col cv-auto"
                >
                  {post.cover_image && (
                    <div className="aspect-video overflow-hidden bg-gray-50">
                      <Image src={post.cover_image} alt={post.title} fittingType="fill" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-bold text-brand-blue">{post.category}</span>
                      <span className="text-gray-400 text-xs flex items-center gap-1">
                        <Clock size={11} /> {post.read_minutes || 5} {t.min}
                      </span>
                    </div>
                    <h2 className="font-heading font-bold text-lg text-gray-900 mb-2 leading-snug group-hover:text-brand-blue transition-colors">{post.title}</h2>
                    <p className="text-gray-500 text-sm font-light leading-relaxed flex-1">{post.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-brand-blue text-sm font-bold group-hover:gap-2.5 transition-all">
                      {t.read} <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            {hasMore && (
              <div className="text-center mt-10">
                <button onClick={loadMore} disabled={loadingMore} className="px-6 py-3 rounded-xl text-sm font-bold text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 transition-all disabled:opacity-50">
                  {loadingMore ? t.loading : t.loadMore}
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}